import React, { useEffect, useState } from 'react';
import {
  ArrowLeft, Plus, Trash2, Loader2, Tag, Percent, BadgePercent, X, Edit3
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { useAdminAuth } from '../hooks/useAdminAuth';
import AdminLogin from './AdminLogin';
import { apiUrl } from '../utils/config';
import logger from '../utils/logger';

const EMPTY_FORM = {
  code: '',
  description: '',
  type: 'percentage',
  value: '',
  scope: 'individual',
  min_subtotal: '',
  max_uses: '',
  one_use_per_email: true,
  starts_at: '',
  expires_at: '',
  active: true,
};

const DiscountCodesAdmin = ({ onBackToHome }) => {
  const { isAuth, isCheckingAuth, handleLogin, handleLogout, getAuthHeaders } = useAdminAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const fmt = (n) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(n || 0);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const r = await fetch(apiUrl('discount-codes'), { headers: getAuthHeaders() });
      if (r.ok) setCodes(await r.json());
    } catch (e) {
      logger.error('Failed to load codes:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuth) fetchCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!isAuth) return <AdminLogin onLogin={handleLogin} title="Discount Codes" />;

  const updateField = (key) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [key]: v }));
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (code) => {
    setForm({
      code: code.code,
      description: code.description || '',
      type: code.type,
      value: String(code.value ?? ''),
      scope: code.scope || 'individual',
      min_subtotal: code.min_subtotal ? String(code.min_subtotal) : '',
      max_uses: code.max_uses ? String(code.max_uses) : '',
      one_use_per_email: !!code.one_use_per_email,
      starts_at: code.starts_at ? code.starts_at.slice(0, 10) : '',
      expires_at: code.expires_at ? code.expires_at.slice(0, 10) : '',
      active: code.active !== false,
    });
    setEditingId(code.id);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) return toast.error('Code is required');
    if (!form.value || Number(form.value) <= 0) return toast.error('Value must be greater than 0');
    if (form.type === 'percentage' && Number(form.value) > 100) {
      return toast.error('Percentage cannot exceed 100');
    }

    const payload = {
      code: form.code,
      description: form.description || '',
      type: form.type,
      value: Number(form.value),
      scope: form.scope,
      min_subtotal: Number(form.min_subtotal || 0),
      max_uses: Number(form.max_uses || 0),
      one_use_per_email: !!form.one_use_per_email,
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      active: !!form.active,
    };

    setSubmitting(true);
    try {
      const url = editingId ? apiUrl(`discount-codes/${editingId}`) : apiUrl('discount-codes');
      const method = editingId ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save');
      }
      toast.success(editingId ? 'Code updated' : 'Code created');
      closeForm();
      fetchCodes();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete code "${code.code}"?`)) return;
    try {
      const r = await fetch(apiUrl(`discount-codes/${code.id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Deleted');
      fetchCodes();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={onBackToHome} className="inline-flex items-center text-gray-600 hover:text-blue-600 text-sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>
          <div className="flex items-center gap-3">
            <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-discount-btn">
              <Plus className="w-4 h-4 mr-2" /> New Code
            </Button>
            <Button variant="outline" onClick={handleLogout}>Log out</Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Tag className="w-6 h-6 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Discount Codes</h1>
        </div>
        <p className="text-gray-600 mb-8">Create coupons for individual (B2C) services. Codes are validated server-side at checkout.</p>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : codes.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
            <BadgePercent className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700">No discount codes yet</h3>
            <p className="text-gray-500 text-sm mb-5">Create your first promo code to start running campaigns.</p>
            <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" /> Create Code
            </Button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-left px-4 py-3">Discount</th>
                  <th className="text-left px-4 py-3">Scope</th>
                  <th className="text-left px-4 py-3">Min cart</th>
                  <th className="text-left px-4 py-3">Used</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => {
                  const expired = c.expires_at && new Date(c.expires_at) < new Date();
                  const limitReached = c.max_uses && c.used_count >= c.max_uses;
                  return (
                    <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`discount-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-mono font-bold text-gray-900">{c.code}</div>
                        {c.description && <div className="text-xs text-gray-500 mt-0.5">{c.description}</div>}
                      </td>
                      <td className="px-4 py-3">
                        {c.type === 'percentage' ? (
                          <span className="inline-flex items-center gap-1 text-blue-700"><Percent className="w-3 h-3" />{c.value}%</span>
                        ) : (
                          <span className="text-blue-700">{fmt(c.value)} off</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 capitalize">{c.scope}</td>
                      <td className="px-4 py-3 text-gray-600">{c.min_subtotal ? fmt(c.min_subtotal) : '—'}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}
                      </td>
                      <td className="px-4 py-3">
                        {!c.active ? (
                          <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">Disabled</span>
                        ) : expired ? (
                          <span className="inline-block px-2 py-0.5 bg-red-50 text-red-700 rounded-full text-xs">Expired</span>
                        ) : limitReached ? (
                          <span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs">Limit reached</span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => openEdit(c)} className="text-blue-600 hover:text-blue-800 mr-3" data-testid={`edit-discount-${c.id}`} aria-label="Edit">
                          <Edit3 className="w-4 h-4 inline" />
                        </button>
                        <button onClick={() => remove(c)} className="text-red-500 hover:text-red-700" data-testid={`delete-discount-${c.id}`} aria-label="Delete">
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">{editingId ? 'Edit code' : 'New discount code'}</h3>
              <button onClick={closeForm} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              {!editingId && (
                <div>
                  <label className="block text-sm font-medium mb-1">Code *</label>
                  <Input value={form.code} onChange={updateField('code')} placeholder="LAUNCH20" required className="uppercase" data-testid="form-code" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea value={form.description} onChange={updateField('description')} rows={2} placeholder="20% off our launch campaign" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Type *</label>
                  <select value={form.type} onChange={updateField('type')} className="w-full border border-gray-300 rounded-md px-3 py-2" data-testid="form-type">
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed amount (₺)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Value *</label>
                  <Input type="number" value={form.value} onChange={updateField('value')} min="0" step="0.01" required data-testid="form-value" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Scope</label>
                  <select value={form.scope} onChange={updateField('scope')} className="w-full border border-gray-300 rounded-md px-3 py-2">
                    <option value="individual">Individual / B2C</option>
                    <option value="all">All services</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min cart (₺)</label>
                  <Input type="number" value={form.min_subtotal} onChange={updateField('min_subtotal')} min="0" placeholder="0 = no minimum" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Max uses</label>
                  <Input type="number" value={form.max_uses} onChange={updateField('max_uses')} min="0" placeholder="0 = unlimited" />
                </div>
                <div className="flex items-center pt-7">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.one_use_per_email} onChange={updateField('one_use_per_email')} className="w-4 h-4" />
                    <span>One use per email</span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Starts</label>
                  <Input type="date" value={form.starts_at} onChange={updateField('starts_at')} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Expires</label>
                  <Input type="date" value={form.expires_at} onChange={updateField('expires_at')} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={updateField('active')} className="w-4 h-4" />
                <span>Active</span>
              </label>

              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <Button type="button" variant="outline" onClick={closeForm} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="form-submit">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : (editingId ? 'Save changes' : 'Create code')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountCodesAdmin;
